import { useParams, Link } from "react-router-dom";
import ResourcePage from "@/components/ResourcePage";
import { useList, fmtRM, dayName } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner, EmptyState, Money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft } from "lucide-react";
import StudentLifecycleCard from "@/pages/admin/StudentLifecycleCard";

export function StudentsPage({ autoOpenCreate = false }) {
  const { data: parents } = useList("/parents");
  const fields = [
    { name: "full_name", label: "Full name", required: true },
    { name: "date_of_birth", label: "Date of birth", type: "date" },
    { name: "gender", label: "Gender", type: "select", options: [
      { value: "female", label: "Female" }, { value: "male", label: "Male" }] },
    { name: "status", label: "Status", type: "select", defaultValue: "active", options: [
      { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" },
      { value: "dropout", label: "Dropout" }] },
    { name: "current_level", label: "Current level", placeholder: "e.g. Grade 2" },
    { name: "start_date", label: "Start date", type: "date" },
    { name: "parent_id", label: "Parent / guardian", type: "select",
      options: (parents || []).map((p) => ({ value: p.id, label: p.full_name })) },
    { name: "relationship_type", label: "Relationship", type: "select", defaultValue: "parent", options: [
      { value: "mother", label: "Mother" }, { value: "father", label: "Father" },
      { value: "parent", label: "Parent" }, { value: "guardian", label: "Guardian" }] },
    { name: "notes", label: "Notes", type: "textarea" },
  ];
  return (
    <ResourcePage
      testId="students-page"
      title="Students"
      description="Enroll students, link parents, and track status (active / dropout history is kept)."
      endpoint="/students"
      autoOpenCreate={autoOpenCreate}
      createLabel="Enroll student"
      searchKeys={["full_name", "parent_names", "current_level"]}
      rowLink={(r) => `/admin/students/${r.id}`}
      columns={[
        { key: "full_name", label: "Name", render: (r) => <span className="font-medium">{r.full_name}</span> },
        { key: "current_level", label: "Level" },
        { key: "parent_names", label: "Parents" },
        { key: "enrollment_date", label: "Enrolled" },
        { key: "status", label: "Status", badge: true },
      ]}
      fields={fields}
      canDelete
    />
  );
}

export function StudentDetailPage() {
  const { id } = useParams();
  const { data: s, loading } = useList(`/students/${id}`);
  if (loading || !s) return <Spinner full />;
  return (
    <div data-testid="student-detail-page">
      <Link to="/admin/students" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> All students
      </Link>
      <PageHeader title={s.full_name} description={`Level: ${s.current_level || "—"} · Enrolled ${s.enrollment_date}`}
        actions={<StatusBadge status={s.status} testId="student-status-badge" />} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <StudentLifecycleCard studentId={id} />
        {s.stats && (
          <div className="rounded-lg border bg-card p-5 lg:col-span-2" data-testid="student-stats-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Attendance & reschedule record</h3>
              {s.stats.dropout_risk && <StatusBadge status="dropout" testId="dropout-risk-badge" />}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4 text-center">
              {[
                ["Attendance", `${s.stats.attendance_rate}%`],
                ["Absences", s.stats.absence_count],
                ["No-shows", s.stats.no_shows],
                ["Reschedules", s.stats.reschedule_requests],
                ["Approved", s.stats.reschedules_approved],
                ["Rejected", s.stats.reschedules_rejected],
                ["Makeup classes", s.stats.makeup_classes],
              ].map(([label, value]) => (
                <div key={label}>
                  <p className="text-xl font-bold font-display">{value}</p>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>
            {s.stats.dropout_risk && (
              <p className="text-xs text-destructive mt-3">
                Dropout risk: repeated absences or reschedules in the last 90 days — consider following up with the parent.
              </p>
            )}
          </div>
        )}
        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-lg font-semibold mb-3">Parents / guardians</h3>
          {s.parents.length === 0 ? <EmptyState text="No parent linked." /> : s.parents.map((p) => (
            <div key={p.id} className="flex justify-between items-center py-2 border-b last:border-0 text-sm">
              <div>
                <p className="font-medium">{p.full_name}</p>
                <p className="text-xs text-muted-foreground">{p.phone} · {p.email}</p>
              </div>
              <Button asChild variant="ghost" size="sm"><Link to={`/admin/parents/${p.id}`}>View</Link></Button>
            </div>
          ))}
        </div>

        <div className="rounded-lg border bg-card p-5">
          <h3 className="text-lg font-semibold mb-3">Weekly classes</h3>
          {s.schedules.length === 0 ? <EmptyState text="No active classes." /> : s.schedules.map((c) => (
            <div key={c.id} className="py-2 border-b last:border-0 text-sm">
              <p className="font-medium">{c.course_name} with {c.teacher_name}</p>
              <p className="text-xs text-muted-foreground">{dayName(c.day_of_week)} {c.start_time}–{c.end_time} · {c.studio_name}</p>
            </div>
          ))}
        </div>

        <div className="rounded-lg border bg-card p-5 lg:col-span-2">
          <h3 className="text-lg font-semibold mb-3">Invoices</h3>
          {s.invoices.length === 0 ? <EmptyState text="No invoices yet." /> : (
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Invoice</TableHead><TableHead>Month</TableHead><TableHead>Total</TableHead>
                <TableHead>Paid</TableHead><TableHead>Outstanding</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {s.invoices.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-mono text-xs">{i.invoice_number}</TableCell>
                    <TableCell>{i.invoice_month}</TableCell>
                    <TableCell><Money value={i.total_fees_collected} /></TableCell>
                    <TableCell><Money value={i.paid_amount} /></TableCell>
                    <TableCell><Money value={i.outstanding_amount} /></TableCell>
                    <TableCell><StatusBadge status={i.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {s.status_history.length > 0 && (
          <div className="rounded-lg border bg-card p-5 lg:col-span-2">
            <h3 className="text-lg font-semibold mb-3">Status history</h3>
            {s.status_history.map((h) => (
              <p key={h.id} className="text-sm py-1.5 border-b last:border-0">
                <StatusBadge status={h.old_status} /> → <StatusBadge status={h.new_status} />
                <span className="text-xs text-muted-foreground ml-2">{h.changed_at?.slice(0, 10)} {h.reason && `· ${h.reason}`}</span>
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
